import { Navigate, RouteObject } from "react-router-dom";

// Layouts
import { AuthLayout, MainLayout, AdminLayout, StaffLayout } from "~/layouts";

import NotFound from "./components/NotFound";
import { CalendarPopup } from "./screens/calendar-popup";

// Auth screens
import {
  LoginView,
  RequestPasswordResetView,
  ResetPasswordView,
} from "~/screens/auth";

// Help & Resources
import { HelpResources } from "~/components/admin/help-resources/help-resources";



// Client screens
import {
  BillingView,
  DocumentScreen,
  Inventory,
  JobsView,
  Overview,
  Profile,
} from "~/screens/client";

// Admin screens
import {
  ScreenDashboard,
  ScreenAHCStorage,
  ScreenAllBookings,
  ScreenBooking,
  ScreenClientInventory,
  ScreenClientList,
  ScreenCopyBooking,
  ScreenDetailBooking,
  ScreenDispatchSummaries,
  ScreenInternalEvents,
  ScreenLoadLists,
  ScreenNewBooking,
  ScreenNotifications,
  ScreenStaff,
  ScreenSupplies,
  ScreenVessel,
  ScreenBackendSetup,
  ScreenInventoryControl,
  ScreenVisualAudits,
  ScreenBillingDashboard,
  ScreenInvoicing,
  ScreenStorageInvoicing,
  ScreenCreateInvoice,
  ScreenQuickBooksIntegration,
  ServiceMetricsScreen,
  ScreenPrintLabels,
  AdminManagementScreen,
  SaasControlScreen,
  ScreenQuickAdd,
  ScreenSales,
  ScreenExpenses,
  ScreenPayroll,
  ScreenExpenseInvoices,
  MissionControl,
  EstimatesList,
  ScreenInventoryTemplates,
  ScreenSupplyKits,
  ScreenAdminProfile,
  AskOzScreen,
} from "./screens/admin";

// Estimate components
import { EstimateDetail } from "~/components/admin/estimates";
import { ScreenAlerts } from "./screens/admin/alerts";
import { BookingsLayout } from "~/layouts/admin/bookings-layout";

// Staff screens
import {
  AssignmentsScreen,
  AvailablityScreen,
  DocumentsScreen,
  InventoryScreen,
  PhotosScreen,
  ProfileScreen,
  ServiceTicketsScreen,
  TimeTrackingScreen,
  CreateDocumentScreen,
  ScreenStaffNotifications,
} from "./screens/staff";

// Types and constants
import { TUser } from "./types";
import { Role } from "./utils/constants";

// Define route configurations outside the function to avoid recreating them on each render
const CLIENT_ROUTES: RouteObject[] = [
  {
    path: "/overview",
    element: <Overview />,
  },
  {
    path: "/inventory",
    element: <Inventory />,
  },
  {
    path: "/documents",
    children: [
      {
        index: true,
        element: <DocumentScreen />,
      },
    ],
  },
  {
    path: "/job",
    element: <JobsView />,
  },
  {
    path: "/billing",
    element: <BillingView />,
  },
  {
    path: "/profile",
    element: <Profile />,
  },
];

const STAFF_ROUTES: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="/assignments" replace />,
  },
  {
    path: "/assignments",
    element: <AssignmentsScreen />,
  },
  {
    path: "/tickets",
    element: <ServiceTicketsScreen />,
  },
  {
    path: "/inventory",
    element: <InventoryScreen />,
  },
  {
    path: "/time-tracking",
    element: <TimeTrackingScreen />,
  },
  {
    path: "/availability",
    element: <AvailablityScreen />,
  },
  {
    path: "/profile",
    element: <ProfileScreen />,
  },
  {
    path: "/notifications",
    element: <ScreenStaffNotifications />,
  },
  {
    path: "/documents",
    children: [
      {
        index: true,
        element: <DocumentsScreen />,
      },
      {
        path: "new",
        element: <CreateDocumentScreen />,
      },
    ],
  },
  {
    path: "/photos",
    element: <PhotosScreen />,
  },
];

const ADMIN_ROUTES: RouteObject[] = [
  {
    index: true,
    element: <ScreenDashboard />,
  },
  {
    path: "/profile",
    element: <ScreenAdminProfile />,
  },
  {
    path: "/alerts",
    element: <ScreenAlerts />,
  },
  {
    path: "/notifications",
    element: <ScreenNotifications />,
  },
  {
    path: "/bookings",
    element: <BookingsLayout />,
    children: [
      {
        index: true,
        element: <ScreenAllBookings />,
      },
      {
        path: "new",
        element: <ScreenNewBooking />,
      },
      {
        path: ":id",
        element: <ScreenDetailBooking />,
      },
      {
        path: "copy",
        children: [
          {
            index: true,
            element: <ScreenCopyBooking />,
          },
          {
            path: ":id",
            element: <ScreenCopyBooking />,
          },
        ],
      },
    ],
  },
  {
    path: "/estimates",
    children: [
      {
        index: true,
        element: <EstimatesList />,
      },
      {
        path: ":id",
        element: <EstimateDetail />,
      },
    ],
  },
  {
    path: "/ahc-storage",
    element: <ScreenAHCStorage />,
  },
  {
    path: "/client",
    children: [
      {
        index: true,
        path: "list",
        element: <ScreenClientList />,
      },
      {
        path: "inventory",
        element: <ScreenClientInventory />,
      },
    ],
  },
  {
    path: "/admin-management",
    element: <AdminManagementScreen />,
  },
  {
    path: "/saas-control",
    element: <SaasControlScreen />,
  },
  {
    path: "/ask-oz",
    element: <AskOzScreen />,
  },
  {
    path: "/help-resources",
    element: <HelpResources />,
  },
  {
    path: "/dispatch",
    children: [
      {
        index: true,
        path: "summaries",
        element: <ScreenDispatchSummaries />,
      },
      {
        path: "load-lists",
        element: <ScreenLoadLists />,
      },
      {
        path: "quick-add",
        element: <ScreenQuickAdd />,
      },
      {
        path: "internal-events",
        element: <ScreenInternalEvents />,
      },
      {
        path: "print-labels",
        element: <ScreenPrintLabels />,
      },
      {
        path: "inventory-control",
        element: <ScreenInventoryControl />,
      },
      {
        path: "visual-audits",
        element: <ScreenVisualAudits />,
      },
    ],
  },
  {
    path: "/operations",
    children: [
      {
        path: "staffing",
        element: <ScreenStaff />,
      },
      {
        path: "vessels-and-shuttles",
        element: <ScreenVessel />,
      },
      {
        path: "supplies",
        element: <ScreenSupplies />,
      },
      {
        path: "supply-kits",
        element: <ScreenSupplyKits />,
      },
      {
        path: "backend-setup",
        element: <ScreenBackendSetup />,
      },
      {
        path: "service-metrics",
        element: <ServiceMetricsScreen />,
      },
      {
        path: "mission-control",
        element: <MissionControl />,
      },
      {
        path: "inventory-templates",
        element: <ScreenInventoryTemplates />,
      },
    ],
  },
  {
    path: "/billing",
    children: [
      {
        path: "dashboard",
        element: <ScreenBillingDashboard />,
      },
      {
        path: "invoicing",
        children: [
          {
            index: true,
            element: <ScreenInvoicing />,
          },
          {
            path: "create",
            element: <ScreenCreateInvoice />,
          },
          {
            path: "create/:bookingId",
            element: <ScreenCreateInvoice />,
          },
          {
            path: ":invoiceId",
            element: <ScreenBooking />,
          },
        ],
      },
      {
        path: "quickbooks",
        element: <ScreenQuickBooksIntegration />,
      },
      {
        path: "sales",
        element: <ScreenSales />,
      },
      {
        path: "expenses",
        element: <ScreenExpenses />,
      },
      {
        path: "expenses/invoices",
        element: <ScreenExpenseInvoices />,
      },
      {
        path: "payroll",
        element: <ScreenPayroll />,
      },
      {
        path: "storage",
        element: <ScreenStorageInvoicing />,
      },
    ],
  },
];

const getRoutes = (user: TUser | null) => {
  // More efficient role checks
  const userRole = user?.role;

  const isAdmin = userRole === Role.ADMIN || userRole === Role.SUPER_ADMIN;
  const isLead = userRole === Role.LEAD;
  const isStaff = userRole === Role.STAFF;
  const isClient = userRole === Role.CLIENT;

  // Choose the appropriate layout based on user role
  const Layout =
    isAdmin || isLead ? AdminLayout : isStaff ? StaffLayout : MainLayout;

  const routes: RouteObject[] = [
    // Calendar popup route (standalone, no layout)
    {
      path: "/calendar-popup",
      element: user ? <CalendarPopup /> : <Navigate to="/login" replace />,
    },
    {
      element: <AuthLayout />,
      children: [
        {
          path: "/login",
          element: <LoginView />,
        },
        {
          path: "/request-password-reset",
          element: <RequestPasswordResetView />,
        },
        {
          path: "/reset-password",
          element: <ResetPasswordView />,
        },
      ],
    },
    {
      path: "/",
      element: user ? <Layout /> : <Navigate to="/login" replace />,
      children: [
        ...(isAdmin ? ADMIN_ROUTES : []),
        ...(isStaff ? STAFF_ROUTES : []),
        ...(isClient ? [
          {
            index: true,
            element: <Navigate to="/overview" replace />,
          },
          ...CLIENT_ROUTES,
        ] : []),
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];
  return routes;
};

export default getRoutes;
